import { reviewTemplate } from "../../shared/review-templates";
import type { ApprovalDTO } from "../../shared/types";

/**
 * Agents often write long questions as one flat line ("1. WHAT IS BEING DECIDED … 2. WHY … - item - item").
 * Text that already has line breaks is left as the agent's own markdown; flat text gets its numbered
 * sections, CAPITALISED labels and dash lists put back on their own lines so it reads as a document.
 */
export function formatApprovalText(text: string): string {
  if (text.includes("\n")) return text;
  return text
    .replace(/(^|\s)(\d{1,2})\.\s+((?:[A-Z][A-Z'&]*\s+)*[A-Z][A-Z'&]+)(?:\s+-\s+|\s+|$)/g, (_, _pre, n, heading) => `\n\n#### ${n}. ${heading}\n\n`)
    .replace(/\s([A-Z][A-Z ]{4,}[A-Z]):\s+/g, (_, label) => `\n\n**${label}:**\n\n`)
    .replace(/([.:)])\s+-\s+/g, "$1\n- ")
    .replace(/(\*\*\n\n)-\s+/g, "$1- ")
    .trim();
}

/** A one-line preview for the list: whitespace collapsed, markdown markers dropped. */
export function previewApprovalText(text: string): string {
  return text.replace(/[#*_`>]+/g, "").replace(/\s+/g, " ").trim();
}

/** Who a pending request still waits for: the person it was asked of, or any one of the agent's owners. */
export function waitingForText(a: Pick<ApprovalDTO, "waitingFor" | "ownerName"> & Partial<Pick<ApprovalDTO, "anyAdmin">>): string {
  if (a.anyAdmin) return "the admins";
  const names = a.waitingFor.map((w) => w.name);
  if (names.length === 0) return a.ownerName ?? "an admin";
  if (names.length === 1) return names[0]!;
  if (names.length > 3) return `${names.slice(0, 2).join(", ")} or ${names.length - 2} others`;
  return `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`;
}

/**
 * Whether this person decides this request: the person it is addressed to, or — when it is addressed to the agent's
 * owners — one of them, and nobody because they are an admin (D14). The same rule the server holds for every kind,
 * read off the row the server filled in, so a screen never offers a verdict or an answer the platform would refuse.
 */
export function canDecideRequest(a: Pick<ApprovalDTO, "waitingFor" | "ownerUserId">, user: { id: string }): boolean {
  return a.ownerUserId ? a.ownerUserId === user.id : a.waitingFor.some((w) => w.id === user.id);
}

/**
 * Who may send again or withdraw an approved item the tool refused: whoever could decide it — its addressee, or for one
 * addressed to the owners any current owner, read off the row the server filled in (not only the one who approved it,
 * and never one who is no longer an owner).
 */
export function canSettleRelease(a: Pick<ApprovalDTO, "waitingFor" | "ownerUserId">, user: { id: string }): boolean {
  return a.ownerUserId ? a.ownerUserId === user.id : a.waitingFor.some((w) => w.id === user.id);
}

/** Who may say they checked the tool after a send that may have gone: its addressee, or any current owner of the agent. */
export function canCheckSend(a: Pick<ApprovalDTO, "waitingFor">, user: { id: string }): boolean {
  return a.waitingFor.some((w) => w.id === user.id);
}

/** A verdict, in one or two words, whatever the kind. A schedule handed over is accepted or declined (AIT-62). */
export function verdictWord(a: Pick<ApprovalDTO, "status"> & Partial<Pick<ApprovalDTO, "kind">>): string {
  if (a.kind === "schedule_handover" && (a.status === "approved" || a.status === "rejected")) return a.status === "approved" ? "Accepted" : "Declined";
  return { pending: "Waiting", approved: "Approved", rejected: "Rejected", answered: "Answered", revision_requested: "Revision requested", withdrawn: "Withdrawn" }[a.status];
}

/** A pending request waiting longer than this is stale: it goes to the top of the list and says how long. */
export const STALE_DAYS = 3;

/** How many days a stale request has waited, or null while it is fresh or no longer waiting. */
export function staleDays(a: Pick<ApprovalDTO, "status" | "createdAt">, now = Date.now()): number | null {
  const age = now - Date.parse(a.createdAt);
  return a.status === "pending" && age > STALE_DAYS * 86_400_000 ? Math.floor(age / 86_400_000) : null;
}

/**
 * Where the platform's send of an approved item stands, for a template that sends something: sent, not sent (the
 * tool refused it, and its addressee sends it again or withdraws it), or maybe sent (the tool failed in a way that
 * cannot say). Null for anything that sends nothing.
 */
export type ReleaseState = "sent" | "unsent" | "uncertain";
export function releaseState(a: Pick<ApprovalDTO, "status" | "template" | "usedAt" | "releaseUncertain" | "releaseError"> & Partial<Pick<ApprovalDTO, "kind">>): ReleaseState | null {
  if (a.status !== "approved") return null;
  if (a.kind !== "action" && (!a.template || !reviewTemplate(a.template)?.release)) return null;
  if (a.releaseUncertain) return "uncertain";
  // Not sent only when the platform tried and the tool said no: an item approved before the platform sent items
  // itself, or one being sent this very second, has nothing to send again.
  return a.usedAt ? "sent" : a.releaseError ? "unsent" : null;
}

/** "Approved", or for an item the platform sends, whether it went. */
export function approvedWord(a: Pick<ApprovalDTO, "status" | "template" | "usedAt" | "releaseUncertain" | "releaseError"> & Partial<Pick<ApprovalDTO, "kind">>): string {
  const state = releaseState(a);
  return state === "sent" ? "Approved · sent" : state === "unsent" ? "Approved · not sent" : state === "uncertain" ? "Approved · may have been sent" : verdictWord(a);
}

/** What approving an item of a template does, in a few words, or that nothing is sent. */
export const releaseText = (release: string | null) =>
  release === "salesos-outreach"
    ? "Approving logs the message in SalesOS Outreach, which sends it"
    : release === "slack-user"
      ? "Approving sends it in Slack as you, exactly as written"
      : "Approving sends nothing; the agent carries on with what was approved";
